<template>
  <div class="content">
    <Background>
      <FriendlierCard>
        <div class="padded-block">
          <div class="logo">
            <LocaleAwareLogo variant="primary" color="cloud" />
          </div>

          <Loading v-if="isLoading" isCenter isFull />

          <div v-else class="text-content">
            <div v-if="validity == null">Something went wrong</div>

            <div v-if="validity == true">
              <div v-if="didFinish" class="profile-complete">
                <h1 class="complete-title is-cloud">{{ t("completeSignup.complete") }}</h1>
                <p class="complete-copy is-cloud">{{ t("completeSignup.downloadApp") }}</p>

                <div class="app-links">
                  <a class="app-link" :href="appStoreUrls.IOS" target="_blank" rel="noopener noreferrer">
                    <img :src="IOS" :alt="t('completeSignup.appStore')" />
                  </a>
                  <a class="app-link" :href="appStoreUrls.ANDROID" target="_blank" rel="noopener noreferrer">
                    <img :src="Android" :alt="t('completeSignup.googlePlay')" />
                  </a>
                </div>

                <p class="complete-copy is-cloud">{{ t("completeSignup.noApp") }}</p>
                <button type="button" class="sunflower-button login-button" @click="login">
                  {{ t("signup.login") }}
                </button>
              </div>

              <div v-else>
                <RegisterForm
                  :title="t('completeSignup.title')"
                  :submit="handleSignup"
                  :buttonText="t('completeSignup.finish')"
                  :locked="isLoading"
                />
              </div>
            </div>

            <div v-if="validity == false">
              <div v-if="!isSent">
                <div class="message">
                  <p v-if="hasParams">{{ t("completeSignup.linkExpired") }}</p>
                  <p v-else>{{ t("completeSignup.newSignup") }}</p>
                </div>

                <form @submit.prevent="handleSendSignupEmail">
                  <div class="friendlier-field">
                    <div class="control">
                      <span class="icon is-medium is-left mr-3">
                        <FontAwesomeIcon icon="envelope" />
                      </span>
                      <input
                        id="email"
                        name="email"
                        v-model="email"
                        type="email"
                        required
                        :placeholder="t('completeSignup.emailPlaceholder')"
                        autocomplete="email"
                        inputmode="email"
                        autocapitalize="off"
                        spellcheck="false"
                        @input="handleEmailInput"
                        @blur="handleEmailBlur"
                        class="is-size-4 is-right p-2"
                      />
                    </div>
                  </div>
                  <p v-if="emailSuggestion" class="email-suggestion">
                    Did you mean
                    <button type="button" class="email-suggestion-link" @click="applyEmailSuggestion">{{ emailSuggestion }}</button>?
                  </p>
                  <div class="mt-4 has-text-danger error">{{ emailError }}</div>
                  <button type="submit" :disabled="!isEmailValid || isLoading" class="sunflower-button is-bold">
                    {{ t("completeSignup.emailSend") }}
                  </button>
                </form>
              </div>

              <div v-else>
                <div class="message">
                  <p>{{ t("completeSignup.emailSent") }}</p>
                </div>
              </div>
            </div>

            <div v-if="error" class="mt-4 has-text-danger error">{{ error }}</div>
          </div>
        </div>
      </FriendlierCard>
    </Background>
  </div>
</template>
<script setup>
import { onMounted, ref, watch, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useFirebase } from "@/revamp/composables";
import { Loading, RegisterForm, Background, FriendlierCard, LocaleAwareLogo } from "@/revamp/components";
import { IOS, Android } from "@/revamp/assets/images";
import { getQueryParams, createValidator, getSuggestedEmail, logError, clearURL } from "@/revamp/utils";
import constants from "@/revamp/utils/constants";
import { notifyStore } from "@/revamp/store";

const APIService = useFirebase();
const Router = useRouter();
const { t } = useI18n();
const validators = createValidator(t);
const appStoreUrls = constants.APP_STORE_URLS;

const hasParams = ref(false);
const isSent = ref(false);
const didFinish = ref(false);
const validity = ref();
const isLoading = ref(true);
const error = ref("");
const willVerify = ref(false);
const destination = ref(null);
const signupLink = ref("");

const email = ref("");
const emailSuggestion = ref(null);
const emailError = ref("");

const isEmailValid = computed(() => {
  return email.value.trim() && !emailError.value;
});

// Email validation
const validateEmail = () => {
  error.value = "";
  emailError.value = "";

  if (!email.value.trim()) {
    emailError.value = "";
    return;
  }
  const validationResult = validators.email(email.value);
  if (!validationResult) {
    emailError.value = "Invalid Email";
  }
};

watch(email, validateEmail);

const handleEmailInput = () => {
  emailSuggestion.value = null;
};

const handleEmailBlur = () => {
  emailSuggestion.value = getSuggestedEmail(email.value);
};

const applyEmailSuggestion = () => {
  if (!emailSuggestion.value) return;
  email.value = emailSuggestion.value;
  emailSuggestion.value = null;
};

const init = async () => {
  try {
    const params = await getQueryParams();

    // If no params, show generic email entry to re-trigger signup flow
    if (
      Object.keys(params).length == 0 ||
      !params.user
    ) {
      hasParams.value = false;
      validity.value = false;
      isLoading.value = false;
      return;
    }

    hasParams.value = true;

    signupLink.value = params.user;

    if (params.verify) {
      willVerify.value = params.verify;
    }
    if (params.dest) {
      destination.value = params.dest;
    }

    const checkResponse = await APIService.checkSignUpLink(signupLink.value);
    if (!checkResponse) {
      error.value = t("errors.genericError");
      validity.value = false;
      return;
    }
    validity.value = checkResponse.isValid;
    if (checkResponse.isValid) {
      email.value = checkResponse.email;
    }
  } catch (e) {
    logError(`CompleteSignup.init: ${e.toString()}`);
    showUnknownError();
  } finally {
    clearURL();
    isLoading.value = false;
  }
};

const handleSignupError = (response) => {
  switch (response.code) {
    case 10000:
      error.value = t("errors.missingFields");
      logError("CompleteSignup.signup: failed to complete signup, missing fields");
      break;
    case 20000:
    case 20001:
      Router.push("FourOhFour");
      break;
    default:
      showUnknownError();
      break;
  }
};

const showUnknownError = async () => {
  notifyStore.createModal(
    t("errors.modalTitle"),
    t("errors.genericError"),
    [
      {
        id: "close",
        text: t("dismiss"),
        action: "close",
      },
    ],
    true
  );
};

const handleSignup = async ({ fname, lname, password, userEmail }) => {
  if (userEmail) {
    email.value = userEmail.toLowerCase().trim();
  }

  try {
    isLoading.value = true;
    const response = await APIService.finishSignup(
      signupLink.value,
      email.value,
      password,
      fname,
      lname,
      willVerify.value
    );

    if (response.error) {
      handleSignupError(response);
      return;
    }
    didFinish.value = response.didFinish;
  } catch (e) {
    logError(`CompleteSignup.Signup: failed to update user info. ${e.toString()}`);
    showUnknownError();
  } finally {
    isLoading.value = false;
  }
};

const login = () => {
  const destinations = {
    friendlier: "https://admin.friendlier.ca",
    client: "https://client.friendlier.ca",
    default: "/",
  };
  const url = destinations[destination.value] || destinations.default;

  if (url.startsWith("http")) {
    window.location.href = url;
  } else {
    Router.push(url);
  }
};

const handleSendSignupEmail = async () => {
  if (!isEmailValid.value) {
    validateEmail();
    return;
  }

  try {
    isLoading.value = true;
    emailSuggestion.value = null;
    const response = await APIService.sendSignupEmail(email.value);

    if (response.isSent) {
      isSent.value = true;
    } else {
      emailError.value = t("errors.emailSendFailed");
    }
  } catch (e) {
    logError(`CompleteSignup.SendSignupEmail: Failed to send email ${e.toString()}`);
    emailError.value = t("errors.emailSendFailed");
  } finally {
    isLoading.value = false;
  }
};

onMounted(init);
</script>
<style lang="scss" scoped>
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";

.logo {
  max-width: 30%;
  min-width: 300px;
}

.profile-complete {
  .complete-title {
    font-size: 1.35rem;
    font-weight: normal;
    line-height: 1.3;
    margin: 28px 0 16px;
  }

  .complete-copy {
    font-size: 1rem;
    line-height: 1.45;
    margin: 0 auto 26px;
    max-width: 270px;
  }

  .app-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-bottom: 26px;

    .app-link {
      display: block;
      flex: 0 1 130px;
      max-width: 130px;

      img {
        display: block;
        width: 100%;
        height: auto;
      }
    }
  }

  .login-button {
    height: auto;
    padding: 0.45em 1.5em;
    border-radius: 8px;
    font-size: 1.1rem;
  }
}

.message {
  p {
    color: $cloud;
    margin: 10px;
  }
  background-color: $denim;
}

.email-suggestion {
  margin-top: 0.5rem;
  color: #4a4a4a;
}

.email-suggestion-link {
  appearance: none;
  border: none;
  background: none;
  color: #0048b4;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}
</style>
